import type { PaginatedResult } from './api';
import type { FrameMaterial, FrameRimType, FrameShape, TargetGroup } from './catalog';
import type { CurrentFaceShape } from './face';

/**
 * Frame recommendations: the contract between the recommendation endpoint and the screens that show it.
 *
 * No score crosses this boundary. A frame carries a qualitative match level, the reason and note codes that explain it, and the rule version that produced it; the screen writes every sentence. Stock appears only when the caller may see stock, and only for branches the caller is authorized for.
 */

export const RecommendationMatchLevel = {
  BEST_MATCH: 'BEST_MATCH',
  GOOD_MATCH: 'GOOD_MATCH',
  OTHER_OPTIONS: 'OTHER_OPTIONS',
  UNRANKED: 'UNRANKED',
} as const;
export type RecommendationMatchLevel =
  (typeof RecommendationMatchLevel)[keyof typeof RecommendationMatchLevel];

/** Where a frame can be had, decided by the server from authorized sellable stock. */
export const RecommendationStockGroup = {
  CURRENT_BRANCH: 'CURRENT_BRANCH',
  OTHER_AUTHORIZED_BRANCH: 'OTHER_AUTHORIZED_BRANCH',
  OUT_OF_STOCK: 'OUT_OF_STOCK',
} as const;
export type RecommendationStockGroup =
  (typeof RecommendationStockGroup)[keyof typeof RecommendationStockGroup];

/** A preference either ranks frames that match or keeps only frames that match. */
export const RecommendationPreferenceMode = {
  PREFER: 'PREFER',
  ONLY: 'ONLY',
} as const;
export type RecommendationPreferenceMode =
  (typeof RecommendationPreferenceMode)[keyof typeof RecommendationPreferenceMode];

/** A size range either ranks frames that match or keeps only frames that match. */
export const RecommendationDimensionMode = {
  RANK: 'RANK',
  ONLY: 'ONLY',
} as const;
export type RecommendationDimensionMode =
  (typeof RecommendationDimensionMode)[keyof typeof RecommendationDimensionMode];

/** Whether stock was looked at, and where. */
export const RecommendationStockScope = {
  CURRENT_BRANCH: 'CURRENT_BRANCH',
  AUTHORIZED_BRANCHES: 'AUTHORIZED_BRANCHES',
} as const;
export type RecommendationStockScope =
  (typeof RecommendationStockScope)[keyof typeof RecommendationStockScope];

/** A reason or note code from the rules, passed through unchanged. */
export interface RecommendationExplanation {
  code: string;
  params: Readonly<Record<string, unknown>>;
}

export interface FrameRecommendationStock {
  group: RecommendationStockGroup;
  currentBranchQuantity: number;
  /** Other branches the caller is authorized for that hold sellable stock. Empty unless other branches were asked for. */
  otherAuthorizedBranches: { branchId: string; branchName: string }[];
}

export interface FrameRecommendationItem {
  variantId: string;
  productId: string;
  sku: string;
  productName: string;
  productNameAr: string | null;
  variantName: string | null;
  brand: { id: string; name: string } | null;
  colourName: string | null;
  frameShape: FrameShape | null;
  lensWidthMm: number | null;
  bridgeWidthMm: number | null;
  templeLengthMm: number | null;
  material: FrameMaterial | null;
  rimType: FrameRimType | null;
  targetGroup: TargetGroup | null;
  priceMinor: number;
  currencyCode: string;
  matchLevel: RecommendationMatchLevel;
  reasons: RecommendationExplanation[];
  notes: RecommendationExplanation[];
  ruleVersion: string;
  /** Null when the caller may not see stock. */
  stock: FrameRecommendationStock | null;
  /**
   * The server's signed statement that this recommendation named this frame for this customer, carried with the frame into a sale.
   *
   * Null when the request names no customer. Opaque to the browser: it grants no permission, price, stock or reservation, and checkout verifies it again before recording where the frame came from.
   */
  selectionToken: string | null;
}

/** What the request asked for, as the rules understood it. */
export interface FrameRecommendationAppliedInputs {
  /** The confirmed face-shape contract behind the style axis; null without a customer or a confirmed shape. */
  face: CurrentFaceShape | null;
  style: boolean;
  dimensions: RecommendationDimensionMode | null;
  frameShapes: RecommendationPreferenceMode | null;
  brandIds: RecommendationPreferenceMode | null;
  colourNames: RecommendationPreferenceMode | null;
  budget: boolean;
  catalogFilters: boolean;
  /** Null when the caller may not see stock, and no stock was read at all. */
  stockScope: RecommendationStockScope | null;
}

export interface FrameRecommendationPage extends PaginatedResult<FrameRecommendationItem> {
  ruleVersion: string;
  currencyCode: string;
  appliedInputs: FrameRecommendationAppliedInputs;
  /** Frames that passed the database hard filters and were evaluated by the rules. */
  evaluatedCount: number;
  /** Frames the rules kept. Pagination counts these. */
  totalEligible: number;
  /** False: every eligible frame is ranked and nothing is cut. */
  truncated: boolean;
}

/** The `typ` claim of a recommendation selection token, so no other token can stand in for one. */
export const RECOMMENDATION_SELECTION_TOKEN_TYPE = 'frame-recommendation-selection';
/** The `v` claim: the layout of the claims below. A different layout is a different version. */
export const RECOMMENDATION_SELECTION_TOKEN_VERSION = 1;
/** The `aud` claim: a selection token is addressed to checkout, never to the API's authentication. */
export const RECOMMENDATION_SELECTION_AUDIENCE = 'optiflow-sales-checkout';

/**
 * The claims of a recommendation selection token: which frame a recommendation named, for whom, under which rules, and why.
 *
 * Ids and codes only. No score, rank, match level, price, stock, filter, budget, face shape or sentence is ever in it, and no expiry: it records what the recommendation said, which does not stop being true with age.
 */
export interface RecommendationSelectionClaims {
  typ: typeof RECOMMENDATION_SELECTION_TOKEN_TYPE;
  v: typeof RECOMMENDATION_SELECTION_TOKEN_VERSION;
  iss: string;
  aud: typeof RECOMMENDATION_SELECTION_AUDIENCE;
  /** Tenant. */
  tid: string;
  /** Customer the frames were recommended for. */
  cid: string;
  /** The recommended frame variant. */
  vid: string;
  /** The rules that produced the recommendation, such as `frame-rules-v1`. */
  rv: string;
  /** The face assessment the style rule read for this frame; null when style was not evaluated for it. */
  fa: string | null;
  /** The reason codes the recommendation gave this frame, unique and sorted by code unit. */
  rc: string[];
  /** When the recommendation was made, in seconds since the epoch. */
  iat: number;
}
